import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { api, AnalyticsDashboardDto, TopPromptDto, UsageTrendDto } from '../../services/api';
import { OverviewCards } from './OverviewCards';
import { UsageTrendsChart } from './UsageTrendsChart';
import { TopPromptsTable } from './TopPromptsTable';
import { CategoryPerformanceChart } from './CategoryPerformanceChart';
import { PromptQualityPanel } from './PromptQualityPanel';
import { AdoptionPanel } from './AdoptionPanel';
import { Alert, AlertDescription } from '../ui/alert';
import { Loader2 } from 'lucide-react';

const TREND_RANGE_OPTIONS = [
  { label: '7 days', days: 7 },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
];

const RANK_BY_OPTIONS: { label: string; value: 'uses' | 'likes' }[] = [
  { label: 'Uses', value: 'uses' },
  { label: 'Likes', value: 'likes' },
];

interface AnalyticsDashboardProps {
  onPromptClick?: (id: number) => void;
  isAdmin?: boolean;
}

export function AnalyticsDashboard({ onPromptClick, isAdmin = false }: AnalyticsDashboardProps = {}) {
  const [data, setData] = useState<AnalyticsDashboardDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trendDays, setTrendDays] = useState(7);
  const [trendData, setTrendData] = useState<UsageTrendDto[]>([]);
  const [trendLoading, setTrendLoading] = useState(false);
  const [rankBy, setRankBy] = useState<'uses' | 'likes'>('uses');
  const [topPromptsData, setTopPromptsData] = useState<TopPromptDto[]>([]);
  const [topPromptsLoading, setTopPromptsLoading] = useState(false);

  useEffect(() => {
    loadAnalyticsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (data) {
      loadTrendData(trendDays);
    }
  }, [trendDays, data]);

  useEffect(() => {
    if (data) {
      loadTopPromptsData(rankBy);
    }
  }, [rankBy, data]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [analyticsData, trends, topPrompts] = await Promise.all([
        api.getAnalyticsDashboard(),
        api.getUsageTrends(trendDays),
        api.getTopPrompts(10, rankBy),
      ]);
      setData(analyticsData);
      setTrendData(trends);
      setTopPromptsData(topPrompts);
    } catch (err) {
      console.error('Failed to load analytics data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const loadTopPromptsData = async (rank: 'uses' | 'likes') => {
    try {
      setTopPromptsLoading(true);
      const topPrompts = await api.getTopPrompts(10, rank);
      setTopPromptsData(topPrompts);
    } catch (err) {
      console.error('Failed to load top prompts:', err);
    } finally {
      setTopPromptsLoading(false);
    }
  };

  const loadTrendData = async (days: number) => {
    try {
      setTrendLoading(true);
      const trends = await api.getUsageTrends(days);
      setTrendData(trends);
    } catch (err) {
      console.error('Failed to load trend data:', err);
    } finally {
      setTrendLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span className="text-lg">Loading analytics...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" className="max-w-2xl mx-auto">
        <AlertDescription>
          Error loading analytics: {error}
          <button 
            onClick={loadAnalyticsData} 
            className="ml-4 underline hover:no-underline"
          >
            Try again
          </button>
        </AlertDescription>
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert className="max-w-2xl mx-auto">
        <AlertDescription>
          No analytics data available
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Analytics Dashboard</h1>
          <p className="text-muted-foreground">
            Comprehensive insights into prompt library performance and usage
          </p>
        </div>
        <button
          onClick={loadAnalyticsData}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
        >
          Refresh Data
        </button>
      </div>

      {/* Overview Stats */}
      <OverviewCards data={data.overviewStats} />

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Usage Trends */}
        <Card className="col-span-1 lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Usage Trends</CardTitle>
                <CardDescription>
                  Activity showing usage patterns, submissions, and engagement
                </CardDescription>
              </div>
              <div className="flex items-center gap-1">
                {TREND_RANGE_OPTIONS.map(({ label, days }) => (
                  <button
                    key={days}
                    onClick={() => setTrendDays(days)}
                    className={`px-3 py-1 text-sm rounded-md transition-colors ${
                      trendDays === days
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {trendLoading ? (
              <div className="flex items-center justify-center h-80">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : (
              <UsageTrendsChart data={trendData} />
            )}
          </CardContent>
        </Card>

        {/* Category Performance */}
        <Card>
          <CardHeader>
            <CardTitle>Category Performance</CardTitle>
            <CardDescription>
              Usage distribution and performance metrics by category
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CategoryPerformanceChart data={data.categoryPerformance} />
          </CardContent>
        </Card>

        {/* Top Prompts */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Top Performing Prompts</CardTitle>
                <CardDescription>
                  Most used and engaged-with prompts in the library
                </CardDescription>
              </div>
              <div className="flex items-center gap-1">
                {RANK_BY_OPTIONS.map(({ label, value }) => (
                  <button
                    key={value}
                    onClick={() => setRankBy(value)}
                    className={`px-3 py-1 text-sm rounded-md transition-colors ${
                      rankBy === value
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {topPromptsLoading ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : (
              <TopPromptsTable data={topPromptsData} onPromptClick={onPromptClick} />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Prompt Quality (FR-7.4) — fetches independently so a failure here never breaks the panels above */}
      <PromptQualityPanel />

      {/* Adoption (FR-8.2) — also self-fetching; non-admins get the aggregate overview only */}
      <AdoptionPanel isAdmin={isAdmin} />
    </div>
  );
}